import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentTemplateDTO, AgentTemplateFormValues } from '../../types';
import { useAgentTemplateAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

const EMPTY_FORM: AgentTemplateFormValues = { Name: '', Content: '', AgentTemplateFormatID: null, AgentTemplateTypeID: null };

/**
 * Restored Templates tab (Phase 2 of the Agent-editor sub-entity restoration — see
 * InstructionsPanel.tsx, this panel's direct structural template). Real entity
 * (AIAgent_AgentTemplates) has no Description field at all — deliberately not rendered here, see
 * types.ts's AgentTemplateDTO comment and DevelopmentHistoryLog.md.
 */
export function TemplatesPanel({ agentId }: { agentId: number }) {
  const api = useAgentTemplateAdminApiClient();
  const [templates, setTemplates] = useState<AgentTemplateDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentTemplateDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentTemplateFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AgentTemplateDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getTemplatesByAgent(agentId);
    setTemplates(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (template: AgentTemplateDTO | 'new') => {
    setEditing(template);
    setForm(
      template === 'new'
        ? EMPTY_FORM
        : {
            Name: template.Name,
            Content: template.Content,
            AgentTemplateFormatID: template.AgentTemplateFormatID ?? null,
            AgentTemplateTypeID: template.AgentTemplateTypeID ?? null,
          }
    );
  };

  const handleSave = async () => {
    if (!form.Name.trim() || !form.Content.trim()) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createTemplate(agentId, form) : await api.updateTemplate((editing as AgentTemplateDTO).AgentTemplateID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deleteTemplate(pendingDelete.AgentTemplateID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setTemplates((prev) => prev.filter((t) => t.AgentTemplateID !== pendingDelete.AgentTemplateID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <div className="aaa-form-group aaa-form-group-full">
          <label>Name *</label>
          <input type="text" value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Content *</label>
          <textarea rows={8} value={form.Content} onChange={(e) => setForm((f) => ({ ...f, Content: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label>Template Format ID</label>
          <input
            type="number"
            value={form.AgentTemplateFormatID ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, AgentTemplateFormatID: e.target.value ? parseInt(e.target.value, 10) : null }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Template Type ID</label>
          <input
            type="number"
            value={form.AgentTemplateTypeID ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, AgentTemplateTypeID: e.target.value ? parseInt(e.target.value, 10) : null }))}
          />
        </div>
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Template'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Template
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && templates.length === 0 && <div className="aaa-entity-dropdown-empty">No templates yet.</div>}
      <div className="aaa-entity-grid">
        {templates.map((template) => (
          <div key={template.AgentTemplateID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{template.Name}</strong>
            </div>
            <p>{template.Content.length > 140 ? `${template.Content.slice(0, 140)}…` : template.Content}</p>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(template)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(template)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Template"
        message={`Delete template "${pendingDelete?.Name}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
